import axios from 'axios/index';
import { loaderEnd, loaderStart } from '../../actions/loader';
import {
  getAccessToken,
  getClient,
  getCurrentUser,
  isLoggedIn,
} from '../../selectors/authentication';
import { logoutRequest } from '../../actions/authentication';

const LOGIN_PATHNAMES = [
  '/login',
  '/set-password',
  '/recover-password',
  '/reset-password',
  '/register',
];

// axios >= 0.30 hands interceptors a config object with a null prototype, which has no hasOwnProperty method.
const loadingDisabled = (config) =>
  config &&
  Object.prototype.hasOwnProperty.call(config, 'loadingDisabled') &&
  !!config.loadingDisabled;

export function configureApi(dispatch, getState) {
  axios.interceptors.request.use((config) => {
    // Custom config to prevent loader to be shown for a asynch request
    if (!loadingDisabled(config)) dispatch(loaderStart());

    let newConfig = config;
    const state = getState();
    if (isLoggedIn(state)) {
      newConfig = { ...config };
      newConfig.headers = {
        ...config.headers,
        'access-token': getAccessToken(state),
        client: getClient(state),
        'token-type': 'bearer',
        uid: getCurrentUser(state).uid,
      };
    }

    return newConfig;
  });

  axios.interceptors.response.use(
    (response) => {
      if (!loadingDisabled(response.config)) dispatch(loaderEnd());
      return response;
    },
    (error) => {
      // `error.response` is undefined whenever a request fails with no HTTP
      // response at all: a dropped connection, DNS failure, CORS rejection,
      // timeout, or an aborted request. Reading through it unguarded threw a
      // TypeError before loaderEnd() could dispatch, so the loader counter
      // never returned to zero and the full-viewport overlay stayed on top of
      // the app - swallowing every click, including on modals - until the user
      // refreshed the page.
      const errorResponse = (error && error.response) || null;
      const errorData = (errorResponse && errorResponse.data) || null;

      // Free the loader first, before anything that could throw. When there is
      // no response, `errorResponse && errorResponse.config` is falsy, so
      // loadingDisabled() is false and loaderEnd() always dispatches.
      if (!loadingDisabled(errorResponse && errorResponse.config)) {
        dispatch(loaderEnd());
      }

      let newError = error;
      const code = errorData && errorData.code;
      const { pathname } = window.location;

      if (
        errorResponse &&
        errorResponse.status === 401 &&
        !LOGIN_PATHNAMES.includes(pathname)
      ) {
        if (pathname === '/admin/dashboard' || code === 're_login') {
          dispatch(logoutRequest());
        } else {
          // redirect user to dashboard
          window.location = '/admin/dashboard';
        }
      } else {
        // A request made with `responseType: 'blob'` - every PDF download in
        // the app - gets its error body back as a Blob. A Blob has no `errors`
        // property, so the check below replaced the whole response with the
        // generic message and destroyed the body before the caller could drain
        // it with `.text()`. The API's real message was thrown away and users
        // saw "An unexpected error occurred." for every failed PDF.
        //
        // Observed on staging: the portfolio weekly report returns
        // 422 {"errors":["No reviewed or published weekly report was submitted
        // for any project of this company in the requested period."]} and the
        // UI showed the generic string instead.
        //
        // Leaving Blob bodies alone only narrows when the replacement fires,
        // so non-blob callers behave exactly as before.
        const isBlobBody = errorData && typeof errorData.text === 'function';

        if (
          !isBlobBody &&
          (!errorData || !errorData.hasOwnProperty('errors'))
        ) {
          newError = {
            ...error,
            response: {
              data: {
                errors: ['An unexpected error occurred.'],
              },
            },
          };
        }
      }

      return Promise.reject(newError);
    }
  );
}
